import { useCallback, useState } from 'react';

import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconEdit } from '@components/icons/edit';
import { IconPerson } from '@components/icons/person';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { capitalizeFirstLetter } from '@utils/strings';

import { User } from 'api/users/requests';
import { useUserWorkspaces, useWorkspaceJourneysCount } from 'api/workspaces/hooks';

import { useEditTeamUserSidebar } from './user-edit-sidebar';

type UserDetailsSidebarProps = {
  user: User;
  users?: User[];
};

const IconRenderer = ({
  iconName,
  color,
  size = 16,
  containerSize = 32,
}: {
  iconName: keyof typeof ICON_CHOOSER_ICONS;
  color: Color;
  size?: number;
  containerSize?: number;
}) => {
  const IconComponent = ICON_CHOOSER_ICONS[iconName] ?? ICON_CHOOSER_ICONS.IconAI;
  return (
    <IconFancyContainer
      size="sm"
      style={{ width: containerSize, height: containerSize }}
      icon={<IconComponent size={size} color={color} />}
      variant={COLOR_TO_VARIANT[color]}
    />
  );
};

const getRoleLabel = (role: string) => {
  return capitalizeFirstLetter(role);
};

export const useUserDetailsSidebar = () => {
  return useSidebar(UserDetailsSidebar, { variant: 'CONTENT_HEAVY', closeOnClickOutside: true });
};

export const UserDetailsSidebar = ({ user, users }: UserDetailsSidebarProps) => {
  const { t } = useScopedTranslate(userDetailsSidebarLocale);
  const showEditUserSidebar = useEditTeamUserSidebar();

  const [currentUser, setCurrentUser] = useState(user);

  const currentIndex = users?.findIndex((u) => u.id === currentUser.id) ?? -1;

  const handlePrevUser = useCallback(() => {
    if (users && currentIndex > 0) {
      setCurrentUser(users[currentIndex - 1]);
    }
  }, [users, currentIndex]);

  const handleNextUser = useCallback(() => {
    if (users && currentIndex < users.length - 1) {
      setCurrentUser(users[currentIndex + 1]);
    }
  }, [users, currentIndex]);

  const handleEditClick = () => {
    showEditUserSidebar({ user: currentUser });
  };

  const joinedDate = currentUser.inviteDate
    ? abbreviateDate(currentUser.inviteDate, 'en')
    : t('noJoinDate');

  const userWorkspaces = useUserWorkspaces(currentUser.shortId);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={currentUser.name} customIcon={<IconPerson color="blue100" />}>
        <Stack orientation="horizontal" space={2}>
          {users && users.length > 1 && (
            <>
              <Button
                variant="ghost"
                iconLeft={<IconArrowLeft />}
                onClick={handlePrevUser}
                aria-label="Previous Member"
                disabled={currentIndex <= 0}
              />
              <Button
                variant="ghost"
                iconLeft={<IconArrowRight />}
                onClick={handleNextUser}
                aria-label="Next Member"
                disabled={currentIndex === -1 || currentIndex >= users.length - 1}
              />
            </>
          )}
          <Button
            variant="ghost"
            iconLeft={<IconEdit />}
            onClick={handleEditClick}
            aria-label={t('editMember')}
          />
        </Stack>
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <Stack space={6}>
          <SectionRow>
            <Text font="bold16" text={t('details')} />
            <SectionContent>
              <Stack space={4}>
                <Avatar size="xxl" src={currentUser.picture} name={currentUser.name} />
                <Stack space={1}>
                  <Text font="semibold14" text={t('fullName')} />
                  <Text font="regular14" text={currentUser.name} />
                </Stack>
                <Stack space={1}>
                  <Text font="semibold14" text={t('email')} />
                  <Text font="regular14" text={currentUser.email} />
                </Stack>
                <Stack space={1}>
                  <Text font="semibold14" text={t('phone')} />
                  <Text
                    font="regular14"
                    text={currentUser.phone ?? t('noPhoneNumber')}
                    color={currentUser.phone ? 'textPrimary' : 'textHelper'}
                  />
                </Stack>
                <Stack space={1}>
                  <Text font="semibold14" text={t('role')} />
                  <Text font="regular14" text={getRoleLabel(currentUser.role)} />
                </Stack>
                <Stack space={1} align="start">
                  <Stack orientation="horizontal" space={2} align="center">
                    <div
                      style={{
                        width: pxToRem(8),
                        height: pxToRem(8),
                        borderRadius: '50%',
                        backgroundColor: theme.color.elementGreen200,
                      }}
                    />
                    <Text font="regular14" text={t('joined')} color="green200" />
                    <Text font="semibold14" text={'•'} color="buttonIconPositive" />
                    <Text font="regular14" text={joinedDate} color="buttonIconPositive" />
                  </Stack>
                </Stack>
              </Stack>
            </SectionContent>
          </SectionRow>

          <Separator />

          <SectionRow>
            <Text font="bold16" text={t('workspaces')} />
            <SectionContent>
              {userWorkspaces.length > 0 ? (
                <Stack orientation="vertical" space={2}>
                  {userWorkspaces.map((workspace) => (
                    <Card key={workspace.shortId} space={4} variant="transparent">
                      <Stack orientation="horizontal" justify="space-between" align="center">
                        <Stack orientation="horizontal" space={4} align="center">
                          <IconRenderer
                            iconName={workspace.icon}
                            color={workspace.color}
                            size={28}
                            containerSize={40}
                          />
                          <Stack orientation="vertical" space={0}>
                            <Text font="bold14" text={workspace.title} />
                            <Text font="regular14" text={workspace.description} />
                          </Stack>
                        </Stack>
                        <WorkspaceJourneysCount workspaceId={workspace.shortId} />
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Text font="regular14" color="textHelper" text={t('noWorkspaces')} />
              )}
            </SectionContent>
          </SectionRow>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const WorkspaceJourneysCount = ({ workspaceId }: { workspaceId: string }) => {
  const { t } = useScopedTranslate(userDetailsSidebarLocale);
  const { data: count = 0, isLoading } = useWorkspaceJourneysCount(workspaceId);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Stack orientation="vertical" space={1} align="center">
      <Text font="bold14" text={count.toString()} />
      <Text font="regular14" text={t('journeys')} />
    </Stack>
  );
};

const SectionRow = styled.div({
  display: 'flex',
  gap: pxToRem(16),
});

const SectionContent = styled.div({
  width: pxToRem(424),
  flexShrink: 0,
  marginLeft: 'auto',
});

const userDetailsSidebarLocale = {
  en: {
    details: 'Details',
    contactInformation: 'Contact Information',
    fullName: 'Full name',
    email: 'Email',
    phone: 'Mobile phone number',
    role: 'Role',
    lastLogin: 'Last Login',
    editMember: 'Edit Member',
    noPhoneNumber: 'No phone number provided',
    noLastLogin: 'Has not logged in yet',
    joined: 'Joined',
    online: 'Online',
    offline: 'Offline',
    teams: 'Teams',
    workspaces: 'Workspaces',
    journeys: 'Journeys',
    noTeams: 'Not assigned to any teams',
    noWorkspaces:
      'This member does not have access to any workspace/s or their workspaces are private',
    noJoinDate: 'Date not available',
    status: 'Status',
  },
};
